import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, ScanLine, Trash2, X, AlertTriangle, CheckCircle2, Package, Lock, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import apiClient from '../../services/apiClient';

const ComboFloorReconciliation = () => {
  const navigate = useNavigate();

  // Floor summary (per-SKU)
  const [summary, setSummary] = useState({ total_bundles: 0, total_usns: 0, total_skus: 0, sku_groups: [] });
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(true);

  // Scan station state
  const [scannedUsns, setScannedUsns] = useState([]); // [{ usn_code, bundle_code, sku_code, design_name, shade, size, status }]
  const [lockedSku, setLockedSku] = useState(null);  // { sku_code, design_name, shade, size }
  const [usnInput, setUsnInput] = useState('');
  const [bundleCode, setBundleCode] = useState('');
  const [bundleValidated, setBundleValidated] = useState(false);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const usnInputRef = useRef(null);
  const bundleInputRef = useRef(null);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await apiClient.get('/combo-reconciliation/floor-summary');
      setSummary({
        total_bundles: res.data.total_bundles || 0,
        total_usns: res.data.total_usns || 0,
        total_skus: res.data.total_skus || 0,
        sku_groups: res.data.sku_groups || [],
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load floor summary');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const resetStation = () => {
    setScannedUsns([]);
    setLockedSku(null);
    setUsnInput('');
    setBundleCode('');
    setBundleValidated(false);
    setNotes('');
    setTimeout(() => usnInputRef.current?.focus(), 50);
  };

  const handleUsnScan = async (raw) => {
    const usn = (raw || '').trim().toUpperCase();
    if (!usn) return;
    if (scannedUsns.some(s => s.usn_code === usn)) {
      toast.error(`USN ${usn} already in this pile`);
      setUsnInput('');
      return;
    }
    try {
      const res = await apiClient.get(`/combo-reconciliation/floor-usn/${encodeURIComponent(usn)}`);
      const b = res.data.bundle;
      const wasConsumed = !!res.data.was_consumed;
      if (lockedSku && b.sku_code !== lockedSku.sku_code) {
        toast.error(
          `USN ${usn} is SKU ${b.sku_code} — pile is locked to ${lockedSku.sku_code}. Submit current pile first.`
        );
        setUsnInput('');
        return;
      }
      const entry = {
        usn_code: usn,
        bundle_code: b.bundle_code,
        sku_code: b.sku_code,
        design_name: b.design_name,
        shade: b.shade,
        size: b.size,
        status: b.status,
        was_consumed: wasConsumed,
      };
      setScannedUsns(prev => [...prev, entry]);
      if (!lockedSku) {
        setLockedSku({
          sku_code: b.sku_code,
          design_name: b.design_name,
          shade: b.shade,
          size: b.size,
        });
        toast.success(`Locked to ${b.sku_code} • ${b.design_name} • ${b.shade} • ${b.size}`);
      }
      if (wasConsumed) {
        toast.info(
          `${usn} was previously CONSUMED — auto-reclaimed and added to pile`,
          { duration: 4000 }
        );
      }
      setUsnInput('');
      setTimeout(() => usnInputRef.current?.focus(), 30);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = (typeof detail === 'object' && detail?.message) || detail || `USN ${usn} not on the combo floor`;
      if (typeof detail === 'object' && detail?.is_missing && detail?.bundle_code) {
        toast.error(msg, {
          duration: 8000,
          action: {
            label: 'Open Investigation Queue',
            onClick: () => {
              navigate(`/wms/sfg/investigation-queue?bundle=${encodeURIComponent(detail.bundle_code)}`);
            },
          },
        });
      } else {
        toast.error(msg);
      }
      setUsnInput('');
    }
  };

  const removeUsn = (usn) => {
    setScannedUsns(prev => {
      const next = prev.filter(s => s.usn_code !== usn);
      if (next.length === 0) setLockedSku(null);
      return next;
    });
  };

  const validateBundle = async () => {
    const code = (bundleCode || '').trim().toUpperCase();
    if (!code) { toast.error('Enter the pre-printed bundle code'); return; }
    try {
      await apiClient.post('/combo-reconciliation/validate-bundle', { bundle_code: code });
      setBundleCode(code);
      setBundleValidated(true);
      toast.success(`${code} is a valid Print Codes bundle`);
    } catch (e) {
      setBundleValidated(false);
      toast.error(e.response?.data?.detail || 'Bundle validation failed');
    }
  };

  const submitWrap = async () => {
    if (!lockedSku || scannedUsns.length === 0) { toast.error('Scan at least one USN first'); return; }
    if (!bundleCode || !bundleValidated) { toast.error('Validate the new bundle code first'); return; }
    setSubmitting(true);
    try {
      const res = await apiClient.post('/combo-reconciliation/quick-wrap', {
        new_bundle_code: bundleCode,
        sku_code: lockedSku.sku_code,
        usn_codes: scannedUsns.map(s => s.usn_code),
        notes,
      });
      const d = res.data;
      const reclaimedCount = (d.reclaimed_from_consumed || []).reduce(
        (s, r) => s + (r.usn_codes_reclaimed || []).length, 0
      );
      const ghostCount = (d.ghost_strips || []).length;
      const parts = [
        `Wrapped ${d.usn_count} USN(s) into ${d.new_bundle_code}`,
        `Deprecated ${d.deprecated_bundles?.length || 0} bundle(s)`,
        `${d.partially_wrapped_bundles?.length || 0} partial`,
      ];
      if (reclaimedCount) parts.push(`${reclaimedCount} reclaimed from CONSUMED`);
      if (ghostCount) parts.push(`${ghostCount} ghost claim${ghostCount > 1 ? 's' : ''} cleared`);
      toast.success(parts.join('. ') + `. Now scan ${d.new_bundle_code} in SFG Put-Away to assign a bin.`);
      resetStation();
      loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Wrap failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Group scanned USNs by source bundle for display
  const scannedBySource = scannedUsns.reduce((acc, s) => {
    (acc[s.bundle_code] = acc[s.bundle_code] || []).push(s.usn_code);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/combo-v2')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">Combo Floor Quick-Wrap</h1>
                <p className="text-sm text-[#E8DCC8]">Scan-driven floor clearance — wrap stuck USNs back to SFG storage</p>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={loadSummary}
            disabled={summaryLoading}
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${summaryLoading ? 'animate-spin' : ''}`} />
            Refresh Floor
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Floor summary */}
        <Card data-testid="floor-summary-card">
          <CardHeader className="cursor-pointer" onClick={() => setSummaryExpanded(v => !v)}>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                {summaryExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                Floor Summary
              </CardTitle>
              <div className="flex gap-2">
                <Badge variant="outline">SKUs: <b className="ml-1">{summary.total_skus}</b></Badge>
                <Badge variant="outline">Bundles: <b className="ml-1">{summary.total_bundles}</b></Badge>
                <Badge variant="outline" className="text-amber-700">USNs: <b className="ml-1">{summary.total_usns}</b></Badge>
              </div>
            </div>
          </CardHeader>
          {summaryExpanded && (
            <CardContent className="p-0">
              {summary.sku_groups.length === 0 ? (
                <p className="text-sm text-gray-500 px-6 py-6 text-center">
                  Floor is empty. Nothing to wrap.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>SKU</TableHead>
                      <TableHead>Design</TableHead>
                      <TableHead>Shade</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead className="text-right">Bundles</TableHead>
                      <TableHead className="text-right">USNs</TableHead>
                      <TableHead>Statuses</TableHead>
                      <TableHead>Sample Bundle Codes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summary.sku_groups.map(g => (
                      <TableRow key={g.sku_code} data-testid={`sku-row-${g.sku_code}`}>
                        <TableCell className="font-mono">{g.sku_code}</TableCell>
                        <TableCell>{g.design_name}</TableCell>
                        <TableCell>{g.shade}</TableCell>
                        <TableCell>{g.size}</TableCell>
                        <TableCell className="text-right">{g.bundle_count}</TableCell>
                        <TableCell className="text-right font-semibold text-amber-700">{g.usn_count}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(g.statuses || []).map(s => (
                              <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs font-mono text-gray-600">
                          {(g.sample_bundles || []).join(', ')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          )}
        </Card>

        {/* Scan station */}
        <Card data-testid="scan-station">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ScanLine className="w-5 h-5" /> Scan Floor USNs
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Locked SKU banner */}
            {lockedSku && (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Lock className="w-4 h-4 text-emerald-700" />
                  <div className="text-sm">
                    <span className="font-mono font-semibold">{lockedSku.sku_code}</span>
                    <span className="text-gray-600 ml-2">{lockedSku.design_name} • {lockedSku.shade} • {lockedSku.size}</span>
                  </div>
                </div>
                <Badge variant="outline" className="bg-white">{scannedUsns.length} USN(s) scanned</Badge>
              </div>
            )}

            {/* USN scan input */}
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">
                {lockedSku ? `Scan next USN (must be SKU ${lockedSku.sku_code})` : 'Scan first USN — locks the SKU for this pile'}
              </label>
              <Input
                ref={usnInputRef}
                placeholder="Scan / type USN code, press Enter"
                value={usnInput}
                onChange={(e) => setUsnInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleUsnScan(usnInput);
                  }
                }}
                className="font-mono text-base"
                autoFocus
                data-testid="usn-scan-input"
              />
            </div>

            {/* Scanned USN list (grouped by source bundle) */}
            {scannedUsns.length > 0 && (
              <div className="border rounded p-3 space-y-3 max-h-[300px] overflow-y-auto">
                {Object.entries(scannedBySource).map(([srcBundle, items]) => {
                  // items here are full entry objects since we now key by bundle but need full objects;
                  // recompute below.
                  const entries = scannedUsns.filter(s => s.bundle_code === srcBundle);
                  const anyConsumed = entries.some(e => e.was_consumed);
                  return (
                    <div key={srcBundle}>
                      <div className="text-xs text-gray-500 mb-1">
                        from <span className="font-mono">{srcBundle}</span> ({entries.length} USN{entries.length > 1 ? 's' : ''})
                        {anyConsumed && (
                          <Badge variant="outline" className="ml-2 text-[10px] text-blue-700 border-blue-300">
                            CONSUMED → reclaimed
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {entries.map(e => (
                          <Badge
                            key={e.usn_code}
                            variant="secondary"
                            className={`font-mono text-xs ${e.was_consumed ? 'bg-blue-50 border-blue-300' : ''}`}
                          >
                            {e.usn_code}
                            <button
                              onClick={() => removeUsn(e.usn_code)}
                              className="ml-1 hover:text-red-600"
                              data-testid={`remove-${e.usn_code}`}
                            >
                              <X className="w-3 h-3 inline" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* New bundle code (only when there are scans) */}
            {scannedUsns.length > 0 && (
              <>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">
                    New bundle code (pre-printed BUNxxxxxx/YY-YY)
                  </label>
                  <div className="flex gap-2">
                    <Input
                      ref={bundleInputRef}
                      placeholder="BUN012345/26-27"
                      value={bundleCode}
                      onChange={(e) => { setBundleCode(e.target.value.toUpperCase()); setBundleValidated(false); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); validateBundle(); } }}
                      className="font-mono"
                      data-testid="bundle-code-input"
                    />
                    <Button variant="outline" onClick={validateBundle} data-testid="validate-bundle-btn">
                      Validate
                    </Button>
                    {bundleValidated && <CheckCircle2 className="w-6 h-6 text-green-600 self-center" />}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Notes (optional, audit trail)</label>
                  <Input
                    placeholder="Reason for this wrap…"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid="notes-input"
                  />
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded p-3 flex gap-2 items-start text-sm text-amber-900">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    On submit: source bundles fully covered by this pile will be marked <b>CONSUMED</b> (deprecated, ignored by all picking). Partially covered bundles will have these USNs stripped. Old session/plan linkages will be broken. The new bundle will be queued as <b>PENDING_PUT_AWAY</b> — scan it in the regular <b>SFG Put-Away</b> module to assign a bin.
                  </div>
                </div>

                <div className="flex justify-between gap-2">
                  <Button variant="outline" onClick={resetStation} data-testid="reset-btn">
                    <Trash2 className="w-4 h-4 mr-2" /> Discard pile
                  </Button>
                  <Button
                    onClick={submitWrap}
                    disabled={submitting || !bundleValidated}
                    className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                    data-testid="submit-wrap-btn"
                  >
                    {submitting ? 'Wrapping…' : `Wrap ${scannedUsns.length} USN(s) into ${bundleCode || '…'}`}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ComboFloorReconciliation;
